import * as React from 'react';
import { Eye, Link as LinkIcon, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DataGrid, type DataGridColumn } from '@/components/ui/data-grid';
import { EmptyState } from '@/components/ui/empty-state';
import type { ClientViewerGrant } from '@/lib/api/settings';

/**
 * External client viewers table (T-0110 / E01-F06).
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/screens-firm.js →
 * window.Screens.firmViewers`. Columns:
 *   - Viewer (name + role/org subline; we only have viewer_name today,
 *     so the subline shows the auth_mode).
 *   - Email (mono).
 *   - Engagement scope (client_name · project_name when scoped, else just
 *     client_name for a whole-client grant).
 *   - Status chip (Active / Invited / Revoked).
 *   - Actions: Preview, Re-issue link (magic_link only), Revoke.
 *
 * The "Preview viewer experience" CTA opens the `ViewerPreviewModal` (see
 * sibling component). The "Re-issue magic link" CTA fires the T-0026
 * reissue endpoint; the "Revoke" CTA fires the revoke endpoint. The parent
 * (`/settings/viewers` route) wires the mutations and toast messages.
 */

export interface ViewersTableProps {
  viewers: ReadonlyArray<ClientViewerGrant>;
  onPreview: (viewer: ClientViewerGrant) => void;
  onReissue?: (viewer: ClientViewerGrant) => void;
  onRevoke?: (viewer: ClientViewerGrant) => void;
  /** Set true while the corresponding viewer's mutation is in flight. */
  pendingGrantId?: string | null;
}

function StatusChip({ status }: { status: string }) {
  if (status === 'active') {
    return (
      <span className="inline-flex h-[22px] items-center rounded-full bg-posture-healthy-tint px-2.5 text-meta font-medium text-posture-healthy">
        Active
      </span>
    );
  }
  if (status === 'invited') {
    return (
      <span className="inline-flex h-[22px] items-center rounded-full bg-posture-at-risk-tint px-2.5 text-meta font-medium text-posture-at-risk">
        Invited
      </span>
    );
  }
  if (status === 'revoked') {
    return (
      <span className="inline-flex h-[22px] items-center rounded-full bg-posture-critical-tint px-2.5 text-meta font-medium text-posture-critical">
        Revoked
      </span>
    );
  }
  return (
    <span className="inline-flex h-[22px] items-center rounded-full bg-ink-100 px-2.5 text-meta font-medium text-ink-700">
      {status}
    </span>
  );
}

export function ViewersTable({
  viewers,
  onPreview,
  onReissue,
  onRevoke,
  pendingGrantId,
}: ViewersTableProps) {
  const columns: ReadonlyArray<DataGridColumn<ClientViewerGrant>> = [
    {
      key: 'viewer',
      header: 'Viewer',
      cell: (v) => (
        <div className="flex flex-col">
          <span className="font-medium text-ink-900">{v.viewer_name}</span>
          <span className="text-meta text-ink-500">
            Auth: {v.auth_mode === 'magic_link' ? 'Magic link' : 'SSO'}
          </span>
        </div>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      cell: (v) => <span className="font-mono text-meta text-ink-700">{v.viewer_email}</span>,
    },
    {
      key: 'scope',
      header: 'Scope',
      cell: (v) => (
        <div className="flex flex-col">
          <span className="text-body-sm text-ink-900">{v.client_name}</span>
          <span className="text-meta text-ink-500">
            {v.project_name ?? <em className="not-italic text-ink-500">Whole client</em>}
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (v) => <StatusChip status={v.status} />,
    },
    {
      key: 'issued',
      header: 'Issued',
      cell: (v) => (
        <span className="font-mono text-meta text-ink-500">{formatIssuedAt(v.created_at)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      cellClassName: 'text-right',
      headerClassName: 'text-right',
      cell: (v) => {
        const pending = pendingGrantId === v.grant_id;
        return (
          <div className="flex justify-end gap-1.5">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => onPreview(v)}
              disabled={pending}
              className="h-7 gap-1 px-2 text-meta"
              aria-label={`Preview viewer experience for ${v.viewer_name}`}
            >
              <Eye className="h-3 w-3" aria-hidden="true" />
              Preview
            </Button>
            {onReissue && v.auth_mode === 'magic_link' && v.status !== 'revoked' ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => onReissue(v)}
                disabled={pending}
                className="h-7 gap-1 px-2 text-meta"
                aria-label={`Re-issue magic link for ${v.viewer_name}`}
              >
                <LinkIcon className="h-3 w-3" aria-hidden="true" />
                {v.status === 'invited' ? 'Resend' : 'Re-issue link'}
              </Button>
            ) : null}
            {onRevoke && v.status !== 'revoked' ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => onRevoke(v)}
                disabled={pending}
                className="h-7 gap-1 px-2 text-meta text-posture-critical hover:bg-posture-critical-tint hover:text-posture-critical"
                aria-label={`Revoke access for ${v.viewer_name}`}
              >
                <X className="h-3 w-3" aria-hidden="true" />
                Revoke
              </Button>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <DataGrid
      columns={columns}
      rows={viewers}
      rowKey={(v) => v.grant_id}
      caption="External viewers"
      emptyState={
        <EmptyState
          title="No external viewers yet"
          description="Invite a client stakeholder via the 'Invite viewer' button above. They will receive a scoped magic-link email."
        />
      }
    />
  );
}

function formatIssuedAt(iso: string): string {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
